<template>
  <div
      v-if="showLoader"
      class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
  >
    <svg
        class="animate-spin rounded-full h-16 w-16 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
    >
      <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
          fill="none"
      />
    </svg>
  </div>
  <div v-else>
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead class="bg-gray-100">
          <tr>
            <td
              v-for="(header, index) in tableHeaders"
              :key="index"
              scope="col"
              class="px-6 py-3 border-b"
            >
              <div class="flex items-center font-semibold text-base">
                {{ header }}
              </div>
            </td>
          </tr>
        </thead>
        <tbody v-if="ffsCompanies.length > 0">
          <tr
            v-for="(item, index) in ffsCompanies"
            :key="index"
            class="border-b text-base"
          >
            <td class="px-6 py-2">{{ item.company_name }}</td>
            <td class="px-6 py-2">{{ item.beneficiaries }}</td>
            <td class="px-6 py-2">
              {{
                new Date(item.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })
              }}
            </td>
            <td class="px-6 py-2">
              <div class="flex space-x-3">
                <button type="button" @click="viewCompanyBeneficiaries(item.id)">
                  <View stroke="orange" fill="none" size="24"/>
                </button>
                <button type="button">
                  <Trash2 @click="triggerDeleteCompany(item.id)" stroke="red" fill="none" size="24"/>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <ConfirmationModal :show="showDeleteConfirmationModal"
                     :title="'Delete Company'"
                     :message="'Are you sure you want to delete this company?'"
                     @confirm="deleteCompany"
                     @cancel="closeDeleteConfirmationModal"
  />
</template>

<script setup>
import { onMounted, ref, defineProps, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from "vue-toast-notification"
import { View, Trash2 } from "lucide-vue-next"
import ConfirmationModal from "@/views/modals/shared/ConfirmationModal.vue";
import apiClient from "@/services/api"

const props = defineProps({
  refresh: {
    type: Boolean,
    required: true
  }
})

const showLoader = ref(false)
const ffsCompanies = ref([])
const companyDeleteLoading = ref(false)
const deletingCompanyId = ref(null)
const showDeleteConfirmationModal = ref(false)


// Pagination reactive data properties
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(null)
const totalPages = ref(null)

const toast = useToast()
const router = useRouter()

const tableHeaders = [
  'Company',
  'Beneficiaries',
  'Created On',
  'Actions'
]

const fetchFFSCompanies = async () => {
  try {
    showLoader.value = true

    const response = await apiClient.fetchFFSCompaniesList({
      page: currentPage.value,
      per_page: itemsPerPage.value
    });

    if (response.success) {
      ffsCompanies.value = response.data
      totalItems.value = response.meta.total_items;
      currentPage.value = response.meta.current_page;
      totalPages.value = response.meta.total_pages;
      itemsPerPage.value = response.meta.per_page;
    }

  } catch (error) {
    toast.error(error || 'Failed to fetch FFS Companies.')
    console.log('failed to fetch ffs companies list.', error)
  } finally {
    showLoader.value = false
 }
}

const viewCompanyBeneficiaries = (company_id) => {
  router.push({ name: "FFSCompanyDetails", params: { id: company_id }})
}

onMounted(() => {
  fetchFFSCompanies();
})

watch(() => props.refresh, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    fetchFFSCompanies();
  }
})

const closeDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = false;
  deletingCompanyId.value = null;
}

const triggerDeleteCompany = (companyId) => {
  deletingCompanyId.value = companyId;
  showDeleteConfirmationModal.value = true;
}

const deleteCompany = async () => {
  try {
    companyDeleteLoading.value = true;
    let response = await apiClient.deleteCompany(deletingCompanyId.value);
    if (response.success) {
      fetchFFSCompanies();
      toast.success("Company deleted successfully");
      showDeleteConfirmationModal.value = false;
    } else {
      showDeleteConfirmationModal.value = false;
      console.error("Failed to delete company:", response.message);
      toast.error(response.message);
    }
  } catch (e) {
    showDeleteConfirmationModal.value = false;
    toast.error(e || "Failed to delete company");
  } finally {
    companyDeleteLoading.value = false;
  }
}
</script>